import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { AnalyticsService } from "@/lib/analytics/analytics.service";
import { useUpgradeNudge } from "@/lib/nudges/useUpgradeNudge.hook";
import { PodcastService } from "@/lib/podcast/podcast.service";

interface MilestoneDetail {
	podcastId: string;
	milestone: number;
}

/**
 * When a user nears the end of a podcast (90% milestone):
 * - Free / trial-eligible users get a value-based trial nudge (N1).
 * - Paying users get the habit-loop "create next / ask documents" prompt.
 * Renders nothing; listens for `podhive:listening-milestone`.
 */
export function PostListenPrompt() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const {
		eligible,
		canShow,
		markShown,
		markClicked,
		markDismissed,
		coinBalance,
	} = useUpgradeNudge();

	const { data: summaryStats } = useQuery({
		queryKey: ["podcast-summary-stats-nudge"],
		queryFn: () => PodcastService.getSummaryStats(),
		enabled: eligible,
		staleTime: 1000 * 60 * 5,
	});

	const promptedRef = useRef(new Set<string>());

	useEffect(() => {
		const handler = (event: Event) => {
			const detail = (event as CustomEvent<MilestoneDetail>).detail;
			if (!detail || detail.milestone !== 90) return;
			if (promptedRef.current.has(detail.podcastId)) return;
			promptedRef.current.add(detail.podcastId);

			// Free users: value-based trial nudge after the aha moment.
			if (eligible && canShow("aha_episode_finished")) {
				const context = {
					podcast_id: detail.podcastId,
					podcast_count: summaryStats?.podcastCount,
					minutes_listened: summaryStats?.totalDurationMinutes,
					coin_balance: coinBalance,
					trigger: "listening_milestone_90",
				};
				markShown("aha_episode_finished", context);

				const description =
					summaryStats && summaryStats.podcastCount > 0
						? t("nudges.aha.descriptionWithStats", {
								count: summaryStats.podcastCount,
								minutes: summaryStats.totalDurationMinutes,
							})
						: t("nudges.aha.description");

				toast(t("nudges.aha.title"), {
					description,
					duration: 15000,
					action: {
						label: t("nudges.aha.cta"),
						onClick: () => {
							markClicked("aha_episode_finished", context);
							navigate("/pricing");
						},
					},
					cancel: {
						label: t("nudges.aha.dismiss"),
						onClick: () => {
							markDismissed("aha_episode_finished", context);
						},
					},
				});
				return;
			}

			// Paying / not eligible: habit-loop create-next prompt.
			AnalyticsService.track("post_listen_prompt_shown", {
				podcast_id: detail.podcastId,
			});

			toast(t("postListen.title"), {
				description: t("postListen.description"),
				duration: 12000,
				action: {
					label: t("postListen.createNext"),
					onClick: () => {
						AnalyticsService.track("post_listen_prompt_clicked", {
							podcast_id: detail.podcastId,
							action: "create_next",
						});
						navigate("/dashboard");
					},
				},
				cancel: {
					label: t("postListen.askDocuments"),
					onClick: () => {
						AnalyticsService.track("post_listen_prompt_clicked", {
							podcast_id: detail.podcastId,
							action: "open_chat",
						});
						window.dispatchEvent(new CustomEvent("podhive:open-global-chat"));
					},
				},
			});
		};

		window.addEventListener("podhive:listening-milestone", handler);
		return () => {
			window.removeEventListener("podhive:listening-milestone", handler);
		};
	}, [
		eligible,
		canShow,
		markShown,
		markClicked,
		markDismissed,
		coinBalance,
		summaryStats,
		navigate,
		t,
	]);

	return null;
}
